/** Members & invitations administration. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import { PersonReference } from "@/features/entities/PersonReference";
import {
  type Invitation,
  type Member,
  MEMBER_KINDS,
  MEMBER_STATUSES,
  useInvitations,
  useInvite,
  useMembers,
  useRevokeInvitation,
  useRoles,
  useUpdateMember,
  useSetMemberCostRate,
} from "@/features/admin/identity/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import { PartyPicker, SitePicker } from "@/shared/components/pickers";
import { formatDateTime, formatRelative } from "@/shared/lib/format";
import { nullable, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select } from "@/shared/ui/form";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

/** Role multi-select as a checkbox list. */
export function RoleCheckboxes({
  value,
  onChange,
  idPrefix,
}: {
  value: string[];
  onChange: (keys: string[]) => void;
  idPrefix: string;
}): ReactElement {
  const roles = useRoles();
  const { i18n } = useTranslation();
  return (
    <div className="flex flex-wrap gap-3" role="group">
      {(roles.data ?? []).map((r) => (
        <Checkbox
          key={r.key}
          id={`${idPrefix}-${r.key}`}
          label={(r.name_i18n as Record<string, string>)[i18n.language] ?? r.name}
          checked={value.includes(r.key)}
          onChange={(e) =>
            onChange(e.target.checked ? [...value, r.key] : value.filter((k) => k !== r.key))
          }
        />
      ))}
    </div>
  );
}

interface InviteValues extends Record<string, unknown> {
  email: string;
  kind: string;
  party_id: string;
}

/** Invite dialog. */
export function InviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const invite = useInvite();
  const [roles, setRoles] = useState<string[]>([]);
  const [scoped, setScoped] = useState(false);
  const [sites, setSites] = useState<string[]>([]);
  const form = useForm<InviteValues>({
    initial: { email: "", kind: "staff", party_id: "" },
    schema: z.object({ email: z.email(t("members.invalidEmail")) }),
    idPrefix: "invite",
  });
  const external = form.values.kind === "vendor" || form.values.kind === "customer";
  const addSite = (id: string | null): void => {
    if (id && !sites.includes(id)) {
      setSites([...sites, id]);
    }
  };
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      invite.mutateAsync({
        email: v.email.trim(),
        roles,
        kind: v.kind,
        party_id: external ? nullable(v.party_id) : null,
        ...(scoped && sites.length ? { site_scope: sites } : {}),
      }),
    );
    if (ok) {
      onOpenChange(false);
    }
  };
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("members.invite")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      submitLabel={t("members.sendInvite")}
    >
      <Field id="invite-email" label={t("auth.email")} error={form.errors.email}>
        <Input type="email" {...form.field("email")} required />
      </Field>
      <Field id="invite-kind" label={t("members.kind")}>
        <Select
          id="invite-kind"
          value={form.values.kind}
          onChange={(e) => form.set("kind", e.target.value)}
        >
          {MEMBER_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`members.kinds.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      {external ? (
        <Field id="invite-party_id" label={t("members.party")} hint={t("members.partyHint")}>
          <PartyPicker
            id="invite-party_id"
            role={form.values.kind}
            value={form.values.party_id}
            onChange={(id) => form.set("party_id", id ?? "")}
          />
        </Field>
      ) : null}
      <Field id="invite-roles" label={t("members.roles")}>
        <RoleCheckboxes idPrefix="invite-role" value={roles} onChange={setRoles} />
      </Field>
      <div className="space-y-2 rounded-md border p-3">
        <Checkbox
          id="invite-scoped"
          label={t("members.restrictToSites")}
          checked={scoped}
          onChange={(e) => setScoped(e.target.checked)}
        />
        {scoped ? (
          <>
            <SitePicker
              id="invite-site_scope"
              value=""
              onChange={addSite}
              placeholder={t("members.addSite")}
            />
            <div className="flex flex-wrap gap-1">
              {sites.map((id) => (
                <Badge key={id} tone="info" className="gap-1">
                  {id.slice(0, 8)}…
                  <button
                    type="button"
                    aria-label={`${t("app.delete")} ${id.slice(0, 8)}`}
                    onClick={() => setSites(sites.filter((s) => s !== id))}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </FormDialog>
  );
}

interface MemberValues extends Record<string, unknown> {
  status: string;
  kind: string;
  party_id: string;
  job_title: string;
  skills: string;
  site_scope: string;
  hourly_rate: string;
}

/** Finance-only rate editor: cannot modify a member's roles, status or site access. */
export function CostRateDialog({
  member,
  onClose,
}: {
  member: Member;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSetMemberCostRate();
  const form = useForm({
    initial: { hourly_rate: member.hourly_rate ?? "" },
    idPrefix: "cost-rate",
  });
  const submit = async (): Promise<void> => {
    if (
      await form.submit((values) =>
        save.mutateAsync({
          id: member.id,
          hourly_rate: values.hourly_rate.trim() === "" ? null : Number(values.hourly_rate),
        }),
      )
    )
      onClose();
  };
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("members.hourlyRate")}
      description={member.user.display_name || member.user.email}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field
        id="cost-rate-hourly_rate"
        label={t("members.hourlyRate")}
        hint={t("members.hourlyRateHint")}
        error={form.errors.hourly_rate}
      >
        <Input type="number" min="0" step="0.01" {...form.field("hourly_rate")} />
      </Field>
    </FormDialog>
  );
}

/** Edit member dialog (roles, status, scope, profile). */
export function MemberDialog({
  member,
  onClose,
}: {
  member: Member;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateMember();
  const { can } = useAuth();
  const manageRate = can("identity.user.cost_rate");
  const [roles, setRoles] = useState<string[]>(member.roles);
  const [scoped, setScoped] = useState(!!member.site_scope?.length);
  const [sites, setSites] = useState<string[]>(member.site_scope ?? []);
  const initial = useMemo<MemberValues>(
    () => ({
      status: member.status,
      kind: member.kind,
      party_id: member.party_id ?? "",
      job_title: member.job_title,
      skills: member.skills.join(", "),
      site_scope: "",
      hourly_rate: member.hourly_rate ?? "",
    }),
    [member],
  );
  const form = useForm<MemberValues>({ initial, idPrefix: "member" });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      update.mutateAsync({
        id: member.id,
        body: {
          status: v.status,
          kind: v.kind,
          roles,
          party_id: nullable(v.party_id),
          job_title: v.job_title,
          skills: v.skills
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          site_scope: scoped ? sites : null,
          ...(manageRate
            ? { hourly_rate: v.hourly_rate.trim() === "" ? null : Number(v.hourly_rate) }
            : {}),
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  const addSite = (id: string | null): void => {
    if (id && !sites.includes(id)) {
      setSites([...sites, id]);
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={member.user.display_name || member.user.email}
      description={member.user.email}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      {manageRate ? (
        <Field
          id="member-hourly_rate"
          label={t("members.hourlyRate")}
          hint={t("members.hourlyRateHint")}
          error={form.errors.hourly_rate}
        >
          <Input type="number" min="0" step="0.01" {...form.field("hourly_rate")} />
        </Field>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="member-status" label={t("app.status")}>
          <Select
            id="member-status"
            value={form.values.status}
            onChange={(e) => form.set("status", e.target.value)}
          >
            {MEMBER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`members.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="member-kind" label={t("members.kind")}>
          <Select
            id="member-kind"
            value={form.values.kind}
            onChange={(e) => form.set("kind", e.target.value)}
          >
            {MEMBER_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`members.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="member-job_title" label={t("members.jobTitle")}>
          <Input {...form.field("job_title")} />
        </Field>
        <Field id="member-skills" label={t("members.skills")} hint={t("app.tagsHint")}>
          <Input {...form.field("skills")} />
        </Field>
      </div>
      {form.values.kind === "vendor" || form.values.kind === "customer" ? (
        <Field id="member-party_id" label={t("members.party")}>
          <PartyPicker
            id="member-party_id"
            role={form.values.kind}
            value={form.values.party_id}
            onChange={(id) => form.set("party_id", id ?? "")}
          />
        </Field>
      ) : null}
      <Field id="member-roles" label={t("members.roles")}>
        <RoleCheckboxes idPrefix="member-role" value={roles} onChange={setRoles} />
      </Field>
      <div className="space-y-2 rounded-md border p-3">
        <Checkbox
          id="member-scoped"
          label={t("members.restrictToSites")}
          checked={scoped}
          onChange={(e) => setScoped(e.target.checked)}
        />
        {scoped ? (
          <>
            <SitePicker
              id="member-site_scope"
              value=""
              onChange={addSite}
              placeholder={t("members.addSite")}
            />
            <div className="flex flex-wrap gap-1">
              {sites.map((id) => (
                <Badge key={id} tone="info" className="gap-1">
                  {id.slice(0, 8)}…
                  <button
                    type="button"
                    aria-label={`${t("app.delete")} ${id.slice(0, 8)}`}
                    onClick={() => setSites(sites.filter((s) => s !== id))}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </FormDialog>
  );
}

/** Pending invitations list. */
function InvitationsTab(): ReactElement {
  const { t } = useTranslation();
  const q = useInvitations();
  const revoke = useRevokeInvitation();
  const columns: ColumnDef<Invitation>[] = [
    { id: "email", header: t("auth.email"), cell: (c) => c.row.original.email },
    {
      id: "kind",
      header: t("members.kind"),
      cell: (c) => t(`members.kinds.${c.row.original.kind}`),
    },
    {
      id: "roles",
      header: t("members.roles"),
      cell: (c) => c.row.original.roles.join(", ") || "—",
    },
    {
      id: "expires",
      header: t("members.expires"),
      cell: (c) => formatDateTime(c.row.original.expires_at),
    },
    {
      id: "actions",
      header: t("app.actions"),
      cell: (c) =>
        c.row.original.accepted_at ? (
          <Badge tone="success">{t("members.accepted")}</Badge>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {c.row.original.invite_url ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void navigator.clipboard?.writeText(c.row.original.invite_url ?? "")}
              >
                {t("members.copyInviteLink")}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => revoke.mutate(c.row.original.id)}
              disabled={revoke.isPending}
            >
              {t("members.revoke")}
            </Button>
          </div>
        ),
    },
  ];
  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return (
    <CompleteDataTable
      columns={columns}
      data={q.data}
      getSearchText={(invitation) => invitation.email}
      searchDescription={t("tablePilot.searchFields", { fields: t("auth.email") })}
      sortFields={[
        { value: "email", label: t("auth.email"), valueOf: (invitation) => invitation.email },
        {
          value: "expires",
          label: t("members.expires"),
          valueOf: (invitation) => invitation.expires_at,
        },
      ]}
      minWidth={700}
      getRowId={(i) => i.id}
      emptyMessage={t("members.noInvitations")}
    />
  );
}

/** Members page. */
export function MembersPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [rateMember, setRateMember] = useState<Member | null>(null);
  const paged = useMembers({ q: search.q, status: search.status });
  const update = (patch: Partial<typeof search>): void =>
    void navigate({ to: "/admin/members", search: { ...search, ...patch }, resetScroll: false });

  const columns: ColumnDef<Member>[] = [
    {
      id: "user",
      header: t("app.name"),
      cell: (c) => (
        <div>
          <div className="font-medium">
            <PersonReference
              id={c.row.original.id}
              name={c.row.original.user.display_name || c.row.original.user.email}
              context={{ kind: "member", onEdit: () => setEditing(c.row.original) }}
            />
          </div>
          <div className="text-xs text-muted-foreground">{c.row.original.user.email}</div>
        </div>
      ),
    },
    {
      id: "kind",
      header: t("members.kind"),
      cell: (c) => t(`members.kinds.${c.row.original.kind}`),
    },
    {
      id: "roles",
      header: t("members.roles"),
      cell: (c) => c.row.original.roles.join(", ") || "—",
    },
    {
      id: "status",
      header: t("app.status"),
      cell: (c) => <StatusBadge value={c.row.original.status} namespace="members.status" />,
    },
    {
      id: "scope",
      header: t("members.siteScope"),
      cell: (c) =>
        c.row.original.site_scope?.length
          ? `${c.row.original.site_scope.length} ${t("members.sites")}`
          : t("app.all"),
    },
    {
      id: "active",
      header: t("members.lastActive"),
      cell: (c) => formatRelative(c.row.original.last_active_at),
    },
    ...(can("identity.user.manage")
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button size="sm" variant="ghost" onClick={() => setEditing(c.row.original)}>
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<Member>,
        ]
      : []),
    ...(can("identity.user.cost_rate")
      ? [
          {
            id: "cost-rate",
            header: t("members.hourlyRate"),
            cell: ({ row }: { row: { original: Member } }): ReactElement => (
              <Button size="sm" variant="outline" onClick={() => setRateMember(row.original)}>
                {t("members.manageCostRate")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title={t("members.title")}
        actions={
          can("identity.user.invite") ? (
            <Button onClick={() => setInviteOpen(true)}>{t("members.invite")}</Button>
          ) : null
        }
      />
      {inviteOpen ? <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} /> : null}
      {editing ? <MemberDialog member={editing} onClose={() => setEditing(null)} /> : null}
      {rateMember ? (
        <CostRateDialog member={rateMember} onClose={() => setRateMember(null)} />
      ) : null}
      <Tabs
        value={search.tab ?? "members"}
        onValueChange={(tab) =>
          update({ tab, q: undefined, status: undefined, sort: undefined, page: undefined })
        }
      >
        <TabsList>
          <TabsTrigger value="members">{t("members.title")}</TabsTrigger>
          <TabsTrigger value="invitations">{t("members.invitations")}</TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <TableToolbar
            filters={[
              {
                id: "status",
                label: t("app.status"),
                kind: "select",
                prominent: true,
                value: search.status,
                options: MEMBER_STATUSES.map((value) => ({
                  value,
                  label: t(`members.status.${value}`),
                })),
                onChange: (value) =>
                  update({ status: typeof value === "string" ? value : undefined }),
                onRemove: () => update({ status: undefined }),
              },
            ]}
            search={search.q}
            searchPlaceholder={t("app.search")}
            searchDescription={t("tablePilot.memberSearchScope")}
            onSearchChange={(q) => update({ q })}
            onReset={() => update({ q: undefined, status: undefined })}
          />
          {paged.query.isPending ? (
            <LoadingState />
          ) : paged.query.isError ? (
            <ErrorState error={paged.query.error} onRetry={() => void paged.query.refetch()} />
          ) : (
            <DataTable
              columns={columns}
              data={paged.items}
              minWidth={900}
              getRowId={(m) => m.id}
              total={paged.total}
              hasMore={paged.hasMore}
              onLoadMore={paged.loadMore}
              isLoadingMore={paged.isLoadingMore}
            />
          )}
        </TabsContent>
        <TabsContent value="invitations">
          <InvitationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
